import React from 'react';
import Link from 'next/link';
import { Star, Store } from 'lucide-react';
import {
  PRODUCTS,
  PRICE_LISTINGS,
  RETAILERS,
  type DogSize,
} from '@/lib/mockData';

interface Props {
  currentProductId: string;
  dogSizes: DogSize[];
}

export default function RelatedProducts({ currentProductId, dogSizes }: Props) {
  const related = PRODUCTS.filter(
    (p) => p.id !== currentProductId && p.dogSizes.some((s) => dogSizes.includes(s))
  ).slice(0, 4);

  if (!related.length) return null;

  return (
    <section>
      <h2 className="font-display text-xl font-bold text-gray-900 mb-5">Produits similaires</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {related.map((product) => {
          const listings = PRICE_LISTINGS.filter(
            (l) => l.productId === product.id && l.stockStatus !== 'out-of-stock'
          ).sort((a, b) => a.price - b.price);
          const best = listings[0];
          const bestRetailer = RETAILERS.find((r) => r.id === best?.retailerId);
          const retailerCount = new Set(listings.map((l) => l.retailerId)).size;

          return (
            <Link
              href={`/product-detail-price-comparison?id=${product.id}`}
              key={`related-${product.id}`}
              className="card group overflow-hidden hover:border-orange-200 hover:-translate-y-0.5 transition-all duration-200"
            >
              <div className="h-32 bg-gradient-to-br from-orange-50 to-amber-50 overflow-hidden">
                <img
                  src={product.imageUrl}
                  alt={product.imageAlt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-3">
                <p className="text-xs font-bold text-orange-500 uppercase tracking-wide">{product.brand}</p>
                <h3 className="font-display font-bold text-gray-900 text-sm line-clamp-2 group-hover:text-orange-600 transition-colors mt-0.5 mb-2">
                  {product.name}
                </h3>
                <div className="flex items-center gap-1 mb-2">
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={`related-${product.id}-star-${s}`}
                        size={10}
                        className={s <= Math.round(product.ratings) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}
                      />
                    ))}
                  </div>
                  <span className="text-xs text-gray-500">{product.ratings}</span>
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <span className="font-display text-lg font-extrabold text-orange-600 tabular-nums">
                      {best ? `${best.price.toFixed(2)} €` : '—'}
                    </span>
                    {best && (
                      <p className="text-xs text-gray-400 tabular-nums">{best.pricePerKg.toFixed(2)} €/kg</p>
                    )}
                  </div>
                  <div className="text-right">
                    {bestRetailer && (
                      <p className="text-xs font-semibold" style={{ color: bestRetailer.logoColor }}>
                        {bestRetailer.name}
                      </p>
                    )}
                    <div className="flex items-center gap-1 justify-end">
                      <Store size={10} className="text-gray-400" />
                      <span className="text-xs text-gray-400">{retailerCount}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}